import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { ClipboardList, ExternalLink, ChevronRight, ChevronDown, Calendar } from "lucide-react";
import { getBoardEntityLabel, getBoardRoutePath, getBoardLabel } from "@/lib/board-types";
import { format } from "date-fns";

interface LinkedInteraction {
  id: string;
  animalId: string;
  eventType: string;
  scoreCode: string | null;
  scoreBand: string | null;
  occurredAt: string;
  author: string | null;
  locationCode: string;
  zoneCode: string;
  rawNote: string | null;
  sourceReportId: string | null;
  sourceEmailId: string | null;
}

interface LinkedTask {
  id: string;
  title: string;
  type: string;
  boardId: string;
  boardName: string;
  statusName: string | null;
  dueDate: string | null;
  linkedVia: "animal" | "interaction" | "both";
  linkedInteractions?: LinkedInteraction[];
}

interface LinkedTasksResponse {
  tasks: LinkedTask[];
  totalCount: number;
}

interface LinkedTasksListProps {
  entityType: "animal" | "interaction";
  entityId: string;
  compact?: boolean;
  hideHeader?: boolean;
  onInteractionClick?: (interaction: LinkedInteraction) => void;
}

function LinkedViaBadge({ linkedVia }: { linkedVia: string }) {
  const label = linkedVia === "both" ? "Both" : linkedVia === "animal" ? "Animal" : "Interaction";
  return (
    <Badge variant="outline" className="text-xs capitalize" data-testid={`badge-linked-via-${linkedVia}`}>
      {label}
    </Badge>
  );
}

export function LinkedTasksList({ entityType, entityId, compact = false, hideHeader = false, onInteractionClick }: LinkedTasksListProps) {
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());

  const { data, isLoading } = useQuery<LinkedTasksResponse>({
    queryKey: [entityType === "animal" ? "/api/tasks/by-animal" : "/api/tasks/by-interaction", entityId],
    enabled: !!entityId,
  });

  const tasks = data?.tasks ?? [];
  const totalCount = data?.totalCount ?? 0;

  function toggleExpand(taskId: string) {
    setExpandedTasks(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  }

  if (isLoading) {
    return (
      <div className="space-y-2" data-testid="linked-tasks-loading">
        {!hideHeader && (
          <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
            <ClipboardList className="w-3 h-3" /> Seen in Tasks & Meetings
          </Label>
        )}
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="linked-tasks-section">
      {!hideHeader && (
        <div className="flex items-center gap-2">
          <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
            <ClipboardList className="w-3 h-3" /> Seen in Tasks & Meetings
          </Label>
          {totalCount > 0 && (
            <Badge variant="secondary" className="text-xs" data-testid="badge-linked-tasks-count">
              {totalCount}
            </Badge>
          )}
        </div>
      )}

      {tasks.length === 0 ? (
        <p className="text-sm text-muted-foreground italic" data-testid="text-no-linked-tasks">
          No linked tasks
        </p>
      ) : (
        <div className="rounded-md border">
          {tasks.map((task, index) => {
            const interactions = task.linkedInteractions ?? [];
            const hasInteractions = interactions.length > 0;
            const isExpanded = expandedTasks.has(task.id);
            const isEven = index % 2 === 1;

            return (
              <div key={task.id} className={isEven ? "bg-muted/30" : ""}>
                <div
                  className="flex items-start gap-2 px-3 py-2 hover-elevate group"
                  data-testid={`row-linked-task-${task.id}`}
                >
                  {hasInteractions && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleExpand(task.id);
                      }}
                      className="shrink-0 mt-0.5 p-0.5 rounded text-muted-foreground"
                      data-testid={`button-expand-task-${task.id}`}
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-3.5 h-3.5" />
                      ) : (
                        <ChevronRight className="w-3.5 h-3.5" />
                      )}
                    </button>
                  )}
                  {!hasInteractions && <div className="w-[18px] shrink-0" />}
                  <Link
                    href={`${getBoardRoutePath(task.type)}/${task.id}`}
                    className="flex-1 min-w-0"
                    data-testid={`link-linked-task-${task.id}`}
                  >
                    <p className={`font-medium truncate ${compact ? "text-xs" : "text-sm"}`} data-testid={`text-task-title-${task.id}`}>
                      {task.title}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                      <span className="text-xs text-muted-foreground" data-testid={`text-task-board-${task.id}`}>
                        {task.boardName}
                      </span>
                      {task.statusName && (
                        <Badge variant="secondary" className="text-xs" data-testid={`badge-task-status-${task.id}`}>
                          {task.statusName}
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-xs" data-testid={`badge-task-type-${task.id}`}>
                        {getBoardEntityLabel(task.type)}
                      </Badge>
                      {entityType === "animal" && (
                        <LinkedViaBadge linkedVia={task.linkedVia} />
                      )}
                      {hasInteractions && (
                        <Badge variant="secondary" className="text-xs" data-testid={`badge-interaction-count-${task.id}`}>
                          {interactions.length} interaction{interactions.length !== 1 ? "s" : ""}
                        </Badge>
                      )}
                      {task.dueDate && (
                        <span className="text-xs text-muted-foreground" data-testid={`text-task-due-${task.id}`}>
                          {getBoardLabel(task.type, "dueDateShort")} {format(new Date(task.dueDate), "MMM d")}
                        </span>
                      )}
                    </div>
                  </Link>
                  <a href={`${getBoardRoutePath(task.type)}/${task.id}`} target="_blank" rel="noopener noreferrer" className="shrink-0 mt-0.5">
                    <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
                  </a>
                </div>

                {isExpanded && interactions.length > 0 && (
                  <div className="border-t border-border/50" data-testid={`expanded-interactions-${task.id}`}>
                    {interactions.map((interaction, intIndex) => (
                      <button
                        key={interaction.id}
                        onClick={() => onInteractionClick?.(interaction)}
                        className={`w-full text-left flex items-center gap-2 pl-10 pr-3 py-1.5 hover-elevate ${intIndex % 2 === 1 ? "bg-muted/30" : ""}`}
                        data-testid={`button-interaction-${interaction.id}`}
                      >
                        <Calendar className="w-3 h-3 text-muted-foreground shrink-0" />
                        <span className="text-xs font-medium">
                          {format(new Date(interaction.occurredAt), "MMM d, yyyy")}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {interaction.eventType}
                        </Badge>
                        {interaction.scoreCode && (
                          <Badge variant="secondary" className="text-xs">
                            {interaction.scoreCode}
                          </Badge>
                        )}
                        {interaction.author && (
                          <span className="text-xs text-muted-foreground truncate">
                            {interaction.author}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {totalCount > 25 && (
            <Link
              href="/tasks"
              className="flex items-center gap-1 text-xs text-muted-foreground hover-elevate px-3 py-2"
              data-testid="link-view-all-tasks"
            >
              View all ({totalCount})
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
